import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import ReportIcon from '@mui/icons-material/Report';
import { MarkdownViewer } from '@nineeng/form-components';
import { format } from 'date-fns';
import { CalendarDays, TriangleAlert } from 'lucide-react';
import { Card } from '@repo/ds';
import { debounce } from 'lodash-es';
import { Spinner, Tabs, DataGrid, RoleChip, Badge, FilterSearch, FilterAutoCompleteById } from '@repo/components';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { PERMISSIONS, SiteAttendee } from '@repo/shared/domain';
import { getVerifyPermission, sortToString, stringToSort } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { usePersonalTasks } from 'src/contexts/PersonalTaskContext';
import { getPersonalDashboardSettings } from 'src/api/admin/settings';
import { getEvents, getOwnEvents } from 'src/api/personal/events';
import { getAttendance, getOwnAttendance } from 'src/api/personal/attendance';
import { UpcomingEventsSection } from 'src/components/UpcomingEventsSection';

export const Route = createFileRoute('/personal_/overview')({
	validateSearch: (search: Record<string, unknown>) => ({
		tab: (search.tab as string) || '0',
		sort: (search.sort as string) || 'timestamp:desc',
		search: search.search as string | undefined,
		zoneIds: search.zoneIds as string | undefined,
		page: search.page !== undefined ? Number(search.page) : 0,
	}),
	component: OverviewPage,
});

function OverviewPage() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { tasksTodo } = usePersonalTasks();
	const { config, loading: configLoading } = useConfig();
	const activeTab = parseInt(search.tab || '0', 10);
	const setActiveTab = (tab: number) => {
		navigate({ to: '/personal/overview', search: { ...search, tab: tab.toString() } });
	};
	const handleSort = (model: { field: string; sort: 'asc' | 'desc' }[]) => {
		const sort = model[0];
		if (!sort) return;
		navigate({ to: '/personal/overview', search: { ...search, sort: sortToString(sort), page: 0 } });
	};
	const handleSearchChange = debounce((searchValue: string) => {
		navigate({ to: '/personal/overview', search: { ...search, search: searchValue || undefined, page: 0 } });
	}, 350);
	const handleFilterChange = (filterData: Record<string, string | string[] | undefined>) => {
		const zoneIds = filterData.zoneIds;
		const joined = Array.isArray(zoneIds) ? zoneIds.filter(Boolean).join(',') : zoneIds;
		navigate({
			to: '/personal/overview',
			search: { ...search, zoneIds: joined || undefined, page: 0 },
		});
	};
	const handlePaginationModelChange = ({ page }: { page: number; pageSize: number }) => {
		navigate({ to: '/personal/overview', search: { ...search, page } });
	};

	useBreadcrumbLevel(0, 'Personal Dashboard');

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const canViewAllEvents = verifyPermission([PERMISSIONS.personalAllEventsView]);
	const canViewOwnEvents = verifyPermission([PERMISSIONS.personalOwnEventsView]);
	const canViewAnyEvents = canViewAllEvents || canViewOwnEvents;
	const featureFlags = config?.featureFlags || {};
	const useCheckinatwork = featureFlags.USE_CHECKINATWORK === true;

	const upcomingEventsParams = useMemo(() => {
		const now = new Date();
		const in30Days = new Date(now);
		in30Days.setDate(in30Days.getDate() + 30);
		return {
			from: now.toISOString(),
			until: in30Days.toISOString(),
			sort: 'startTime:asc',
			archivedFilter: 'active-events',
			noPagination: 'true',
		};
	}, []);

	const { data: upcomingEventsData } = useQuery({
		queryKey: ['personal-overview-upcoming-events', upcomingEventsParams, canViewAllEvents, canViewOwnEvents],
		queryFn: () => (canViewAllEvents ? getEvents(upcomingEventsParams) : getOwnEvents(upcomingEventsParams)),
		enabled: canViewAnyEvents,
	});

	const upcomingEvents = upcomingEventsData?.data ?? [];
	const upcomingEventsCount = upcomingEventsData?.count ?? 0;
	const nonCompliantEventsCount = useMemo(
		() => upcomingEvents.filter((e) => e.status !== 'accepted').length,
		[upcomingEvents],
	);

	const { data: settings, isLoading: settingsLoading } = useQuery({
		queryKey: ['personal-dashboard-settings'],
		queryFn: () => getPersonalDashboardSettings(),
	});

	const {
		data: attendanceResponse,
		isLoading: attendanceLoading,
		refetch: refetchAttendance,
	} = useQuery({
		queryKey: ['personal-attendance', canViewAllEvents, canViewOwnEvents],
		queryFn: () =>
			canViewAllEvents ? getAttendance({ sort: 'timestamp:desc' }) : getOwnAttendance({ sort: 'timestamp:desc' }),
		enabled: activeTab === 1 && canViewAnyEvents,
	});
	const attendanceDataRaw = (attendanceResponse?.data ?? []) as SiteAttendee[];

	const attendanceZones = useMemo(() => {
		const seen = new Map<string, string>();
		for (const row of attendanceDataRaw) {
			if (row.zoneId && row.zoneName && !seen.has(row.zoneId)) {
				seen.set(row.zoneId, row.zoneName);
			}
		}
		return Array.from(seen, ([id, name]) => ({ id, name }));
	}, [attendanceDataRaw]);

	const attendanceData = useMemo(() => {
		let filtered = attendanceDataRaw;
		if (search.search) {
			const q = search.search.toLowerCase().trim();
			filtered = filtered.filter(
				(row) => row.firstName?.toLowerCase().includes(q) || row.lastName?.toLowerCase().includes(q),
			);
		}
		if (search.zoneIds) {
			const zoneIdSet = new Set(search.zoneIds);
			filtered = filtered.filter((row) => row.zoneId && zoneIdSet.has(row.zoneId));
		}
		return filtered;
	}, [attendanceDataRaw, search.search, search.zoneIds]);

	const attendanceColumns = useMemo(
		() => [
			{
				field: 'markedForReview',
				headerName: '',
				renderCell: (c: { value: unknown; row: SiteAttendee }) =>
					c.row.markedForReview && <ReportIcon fontSize="small" color="error" />,
				width: 5,
				sortable: false,
			},
			{
				field: 'firstName',
				headerName: 'First Name',
				flex: 1,
				sortable: false,
			},
			{
				field: 'lastName',
				headerName: 'Last Name',
				flex: 1,
				sortable: false,
			},
			{
				field: 'zoneName',
				headerName: 'Zone',
				flex: 1,
				sortable: false,
			},
			{
				field: 'roles',
				headerName: 'Roles',
				flex: 1,
				sortable: false,
				renderCell: (c: { value: unknown; row: SiteAttendee }) => {
					const roles = c.value as { name: string; status: string }[] | undefined;
					return roles?.map((r) => (
						<RoleChip
							key={r.name}
							role={r as { name: string; status: 'pending' | 'accepted' | 'expired' | 'expiryWarning' | 'archived' }}
							userMarkedForReview={!!c.row.markedForReview || c.row.status === 'blocked'}
						/>
					));
				},
			},
			{
				field: 'groupNames',
				headerName: 'Company',
				flex: 1,
				sortable: false,
				renderCell: (c: { value: unknown; row: SiteAttendee }) => {
					const groupNames = c.value as string[] | undefined;
					const res = [...(groupNames || []), (c.row as Record<string, unknown>).company].filter(Boolean);
					return res.join(' / ');
				},
			},
			...(useCheckinatwork
				? [
						{
							field: 'ciawStatus',
							headerName: 'CIAW Status',
							flex: 1,
							sortable: false,
						},
						{
							field: 'ciawId',
							headerName: 'CIAW ID',
							flex: 1,
							sortable: false,
						},
					]
				: []),
			{
				field: 'timestamp',
				headerName: 'Timestamp',
				flex: 1,
				valueFormatter: (params: { value: unknown }) =>
					params.value ? format(new Date(params.value as string), 'dd/MM/yyyy HH:mm') : '',
			},
			{
				field: 'direction',
				headerName: '',
				width: 135,
				sortable: false,
				renderCell: (c: { value: unknown; row: SiteAttendee }) => (
					<Badge variant={c.row.direction === 'in' ? 'success' : 'default'}>
						{c.row.direction === 'in' ? 'Checked In' : 'Checked Out'}
					</Badge>
				),
			},
		],
		[useCheckinatwork],
	);

	const isLoading = authLoading || settingsLoading || configLoading;

	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const welcomeMessage =
		settings?.settings?.['welcomeMessage'] ||
		`# Welcome
This is the default welcome message`;
	const helpLinks = settings?.settings?.helpLinks || [];
	const links = settings?.settings?.links || [];

	return (
		<div className="px-4">
			<div className="mt-2 flex items-center justify-start flex-col">
				{tasksTodo.length > 0 && (
					<Alert
						severity="info"
						variant="filled"
						sx={{
							maxWidth: 1280,
							width: '100%',
							marginTop: 2,
							marginBottom: 2,
							'& ul': { margin: 0, paddingLeft: 2 },
						}}
						action={
							<Button
								color="inherit"
								size="small"
								onClick={() => navigate({ to: '/personal/tasks' })}
								sx={{ fontWeight: 'bold' }}
							>
								View Tasks
							</Button>
						}
					>
						You have open tasks to complete
					</Alert>
				)}
				<div className="max-w-[1280px] w-full flex gap-4">
					<div className="min-w-[640px]">
						<Card className="p-4 rounded-xl">
							<h1 className="font-bold">Your Profile</h1>
							<p>
								{authenticatedUser?.firstName} {authenticatedUser?.lastName}
							</p>
							<p>{authenticatedUser?.email}</p>
						</Card>
						{links.length > 0 && (
							<Card className="p-4 mt-4 rounded-xl">
								<p className="font-bold">Links</p>
								<div className="flex flex-col gap-1">
									{links.map((link) =>
										link.label && link.url ? (
											<p key={link.label}>
												<a
													href={link.url}
													target="_blank"
													rel="noopener noreferrer"
													className="text-primary-600 hover:text-primary-700"
												>
													{link.label}
												</a>
											</p>
										) : null,
									)}
								</div>
							</Card>
						)}
						{helpLinks.length > 0 && (
							<Card className="p-4 mt-4 rounded-xl">
								<p className="font-bold mb-1">Get Help</p>
								<div className="flex flex-col gap-1">
									{helpLinks.map((link) =>
										link.label && link.url ? (
											<p key={link.label}>
												<a
													href={link.url}
													target="_blank"
													rel="noopener noreferrer"
													className="text-primary-600 hover:text-primary-700"
												>
													{link.label}
												</a>
											</p>
										) : null,
									)}
								</div>
							</Card>
						)}
					</div>
					<Card className="p-4 flex-1 nine-task-overrides rounded-xl">
						<MarkdownViewer value={welcomeMessage} />
					</Card>
				</div>
				<div className="max-w-[1280px] w-full mt-8 border-b border-gray-300">
					<Tabs variant="underline" value={activeTab} onChange={setActiveTab}>
						<Tabs.TabList>
							<Tabs.Tab value={0} label="Upcoming Events" />
							<Tabs.Tab value={1} label="Attendance" />
						</Tabs.TabList>
					</Tabs>
				</div>
				<p className="max-w-[1280px] w-full text-sm text-gray-500 mt-2">
					{activeTab === 0 ? 'Ongoing and upcoming events for the next 30 days' : 'Users from ongoing events'}
				</p>

				{activeTab === 0 && (
					<>
						<div className="max-w-[1280px] w-full flex gap-4 mt-4">
							<Card className="flex-1 flex items-center gap-4 p-5 rounded-xl">
								<div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-50">
									<CalendarDays className="w-5 h-5 text-green-600" />
								</div>
								<div>
									<p className="text-2xl font-bold text-gray-900">{upcomingEventsCount}</p>
									<p className="text-sm text-gray-500">Upcoming Events</p>
								</div>
							</Card>
							<Card className="flex-1 flex items-center gap-4 p-5 rounded-xl">
								<div className="flex items-center justify-center w-10 h-10 rounded-xl bg-red-50">
									<TriangleAlert className="w-5 h-5 text-red-500" />
								</div>
								<div>
									<p className="text-2xl font-bold text-gray-900">{nonCompliantEventsCount}</p>
									<p className="text-sm text-gray-500">Non-Compliant Events</p>
								</div>
							</Card>
						</div>
						<UpcomingEventsSection
							events={upcomingEvents}
							canOpenGroupDetail={verifyPermission([PERMISSIONS.personalGroupsView])}
						/>
					</>
				)}

				{activeTab === 1 && (
					<>
						<div className="max-w-[1280px] w-full flex flex-row flex-wrap gap-2 items-center mt-4">
							<FilterSearch
								label="Search ..."
								defaultValue={search.search || ''}
								onChange={handleSearchChange}
								sx={{ width: '20%', minWidth: '40%', flexGrow: 1 }}
							/>
							<FilterAutoCompleteById
								key="zoneFilter"
								label="Filter Zones"
								options={attendanceZones}
								value={search.zoneIds ? search.zoneIds.filter(Boolean) : []}
								onChange={(zoneIds: string | string[]) => handleFilterChange({ zoneIds: zoneIds as string[] })}
								multiple
								sx={{ width: '20%', minWidth: '40%', flexGrow: 1 }}
							/>
						</div>
						<Paper variant="outlined" className="mt-4 max-w-[1280px] w-full">
							<DataGrid
								data={attendanceData}
								columns={attendanceColumns}
								rowCount={attendanceData.length}
								isLoading={attendanceLoading}
								pagination
								paginationMode="client"
								sortingMode="client"
								sortingOrder={['asc', 'desc']}
								sortModel={[stringToSort((search.sort || 'timestamp:desc') as `${string}:${string}`)]}
								onSortModelChange={handleSort}
								pageSize={15}
								page={search.page || 0}
								onPaginationModelChange={handlePaginationModelChange}
								onReload={() => refetchAttendance()}
							/>
						</Paper>
					</>
				)}
			</div>
		</div>
	);
}
